<script setup lang="ts">
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { User } from '@/composables/useUsersData'

const props = defineProps<{
    user: User
    expanded: boolean
}>()

defineEmits<{
    (e: 'toggle-expand'): void
}>()

const { t } = useI18n()

const displayName = computed(() => {
    const full = `${props.user.firstName} ${props.user.lastName}`.trim()
    if (full) return full
    if (props.user.username) return props.user.username
    return t('users.list.fullNameFallback')
})
</script>

<template>
    <article
        class="bg-white border border-gray-200 rounded-12 p-3 flex flex-col gap-2 transition"
        :class="[
            { 'border-teal': expanded, 'opacity-50': !user.isActive }
        ]"
    >
        <button
            type="button"
            class="text-left flex items-start gap-2"
            @click="$emit('toggle-expand')"
        >
            <!--
                Active/inactive dot — green when active, neutral gray when
                inactive (the card opacity also drops to 50%, mirroring the
                row-class behavior of the desktop table).
            -->
            <span
                class="mt-1.5 w-2 h-2 rounded-full shrink-0"
                :class="user.isActive ? 'bg-teal' : 'bg-gray-300'"
                :title="user.isActive ? t('users.list.cols.active') : ''"
            />
            <div class="flex-1 min-w-0">
                <div class="flex items-center gap-2 flex-wrap">
                    <span class="font-semibold text-ink truncate">{{ displayName }}</span>
                    <span
                        v-if="user.isSuperuser"
                        class="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-semibold bg-orange-l text-orange-d"
                    >
                        {{ t('users.list.cols.admin') }}
                    </span>
                </div>
                <div class="mt-0.5 text-sm text-gray-500 truncate">{{ user.email }}</div>
                <div class="mt-0.5 text-xs text-gray-400 font-mono truncate">{{ user.username }}</div>
            </div>
        </button>
    </article>
</template>
